import { useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { agentDisplayName, type AgentId } from '@/config/env'
import { useConversationContext } from '@/contexts/ConversationContext'
import { useConversationNav } from '@/hooks/useConversationNav'
import { useHannaEnabled } from '@/hooks/useHannaEnabled'

const AGENT_IDS = ['david', 'hanna'] as const satisfies readonly AgentId[]

// Full class strings so Tailwind can see them; each active chip wears its agent's colour.
const ACTIVE_CHIP_CLASS = {
  david: 'bg-david-ui-bg text-david-solid hover:bg-david-ui-bg',
  hanna: 'bg-hanna-ui-bg text-hanna-solid hover:bg-hanna-ui-bg',
} as const satisfies Record<AgentId, string>

export function AgentSwitcherChips() {
  const hannaEnabled = useHannaEnabled()
  const { agentId, streamingState } = useConversationContext()
  const { goToNewChat } = useConversationNav()

  const handleClick = useCallback(
    (target: AgentId) => {
      if (target === agentId) return
      goToNewChat(target)
    },
    [agentId, goToNewChat]
  )

  return (
    <div className="flex items-center gap-1" data-slot="agent-switcher-chips">
      {hannaEnabled &&
        AGENT_IDS.map((id) => {
          const active = agentId === id
          return (
            <Button
              key={id}
              type="button"
              variant="ghost"
              size="sm"
              disabled={streamingState.isStreaming}
              onClick={() => handleClick(id)}
              aria-pressed={active}
              aria-label={agentDisplayName(id)}
              className={cn(
                'h-7 px-2.5 rounded-lg text-xs font-medium',
                active
                  ? cn(ACTIVE_CHIP_CLASS[id], 'cursor-default')
                  : 'bg-transparent text-muted-foreground hover:bg-black/5'
              )}>
              {agentDisplayName(id)}
            </Button>
          )
        })}
    </div>
  )
}
